import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, TrendingUp, TrendingDown, AlertTriangle, BarChart3, Truck, Ship, Plane, Building, Target, Users, Package, Clock, Calendar } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
const MonthlyLogisticsInsightsArticle = () => {
  const {
    t
  } = useLanguage();

  // European Road Freight Index & Fuel Price Data (Jan 2022 - Oct 2025)
  const roadFreightData = [{
    month: 'Jan 2022',
    freightIndex: 98.5,
    fuelPrice: 1420
  }, {
    month: 'Feb 2022',
    freightIndex: 102.1,
    fuelPrice: 1450
  }, {
    month: 'Mar 2022',
    freightIndex: 115.3,
    fuelPrice: 1680
  }, {
    month: 'Apr 2022',
    freightIndex: 118.7,
    fuelPrice: 1720
  }, {
    month: 'May 2022',
    freightIndex: 121.2,
    fuelPrice: 1750
  }, {
    month: 'Jun 2022',
    freightIndex: 124.8,
    fuelPrice: 1820
  }, {
    month: 'Jul 2022',
    freightIndex: 127.3,
    fuelPrice: 1780
  }, {
    month: 'Aug 2022',
    freightIndex: 125.9,
    fuelPrice: 1760
  }, {
    month: 'Sep 2022',
    freightIndex: 128.4,
    fuelPrice: 1790
  }, {
    month: 'Oct 2022',
    freightIndex: 130.1,
    fuelPrice: 1850
  }, {
    month: 'Nov 2022',
    freightIndex: 132.7,
    fuelPrice: 1880
  }, {
    month: 'Dec 2022',
    freightIndex: 129.8,
    fuelPrice: 1820
  }, {
    month: 'Jan 2023',
    freightIndex: 126.4,
    fuelPrice: 1750
  }, {
    month: 'Feb 2023',
    freightIndex: 124.2,
    fuelPrice: 1720
  }, {
    month: 'Mar 2023',
    freightIndex: 122.8,
    fuelPrice: 1690
  }, {
    month: 'Apr 2023',
    freightIndex: 120.5,
    fuelPrice: 1650
  }, {
    month: 'May 2023',
    freightIndex: 118.9,
    fuelPrice: 1620
  }, {
    month: 'Jun 2023',
    freightIndex: 117.3,
    fuelPrice: 1590
  }, {
    month: 'Jul 2023',
    freightIndex: 119.7,
    fuelPrice: 1630
  }, {
    month: 'Aug 2023',
    freightIndex: 121.4,
    fuelPrice: 1670
  }, {
    month: 'Sep 2023',
    freightIndex: 123.8,
    fuelPrice: 1710
  }, {
    month: 'Oct 2023',
    freightIndex: 125.6,
    fuelPrice: 1740
  }, {
    month: 'Nov 2023',
    freightIndex: 127.2,
    fuelPrice: 1680
  }, {
    month: 'Dec 2023',
    freightIndex: 124.9,
    fuelPrice: 1640
  }, {
    month: 'Jan 2024',
    freightIndex: 122.3,
    fuelPrice: 1610
  }, {
    month: 'Feb 2024',
    freightIndex: 120.8,
    fuelPrice: 1580
  }, {
    month: 'Mar 2024',
    freightIndex: 123.5,
    fuelPrice: 1620
  }, {
    month: 'Apr 2024',
    freightIndex: 126.7,
    fuelPrice: 1660
  }, {
    month: 'May 2024',
    freightIndex: 129.4,
    fuelPrice: 1700
  }, {
    month: 'Jun 2024',
    freightIndex: 131.8,
    fuelPrice: 1730
  }, {
    month: 'Jul 2024',
    freightIndex: 134.2,
    fuelPrice: 1650
  }, {
    month: 'Aug 2024',
    freightIndex: 132.9,
    fuelPrice: 1620
  }, {
    month: 'Sep 2024',
    freightIndex: 131.5,
    fuelPrice: 1590
  }, {
    month: 'Oct 2024',
    freightIndex: 133.1,
    fuelPrice: 1640
  }, {
    month: 'Nov 2024',
    freightIndex: 134.7,
    fuelPrice: 1680
  }, {
    month: 'Dec 2024',
    freightIndex: 132.4,
    fuelPrice: 1650
  }, {
    month: 'Jan 2025',
    freightIndex: 130.8,
    fuelPrice: 1620
  }, {
    month: 'Feb 2025',
    freightIndex: 129.6,
    fuelPrice: 1590
  }, {
    month: 'Mar 2025',
    freightIndex: 131.2,
    fuelPrice: 1630
  }, {
    month: 'Apr 2025',
    freightIndex: 132.8,
    fuelPrice: 1670
  }, {
    month: 'May 2025',
    freightIndex: 132.2,
    fuelPrice: 1650
  }, {
    month: 'Jun 2025',
    freightIndex: 132.2,
    fuelPrice: 1640
  }, {
    month: 'Jul 2025',
    freightIndex: 134.06,
    fuelPrice: 1680
  }, {
    month: 'Aug 2025',
    freightIndex: 133.8,
    fuelPrice: 1690
  }, {
    month: 'Sep 2025',
    freightIndex: 133.5,
    fuelPrice: 1700
  }, {
    month: 'Oct 2025',
    freightIndex: 134.2,
    fuelPrice: 1705
  }];

  // Shanghai Containerized Freight Index Data (Jan 2022 - Oct 2025) - USD/TEU
  const scfiData = [{
    month: 'Jan 2022',
    global: 4850,
    asiaNorthEurope: 8200,
    asiaMediterranean: 9100
  }, {
    month: 'Feb 2022',
    global: 5200,
    asiaNorthEurope: 8800,
    asiaMediterranean: 9800
  }, {
    month: 'Mar 2022',
    global: 4900,
    asiaNorthEurope: 8400,
    asiaMediterranean: 9200
  }, {
    month: 'Apr 2022',
    global: 4650,
    asiaNorthEurope: 7900,
    asiaMediterranean: 8700
  }, {
    month: 'May 2022',
    global: 4200,
    asiaNorthEurope: 7200,
    asiaMediterranean: 8000
  }, {
    month: 'Jun 2022',
    global: 3800,
    asiaNorthEurope: 6500,
    asiaMediterranean: 7200
  }, {
    month: 'Jul 2022',
    global: 3400,
    asiaNorthEurope: 5800,
    asiaMediterranean: 6400
  }, {
    month: 'Aug 2022',
    global: 3100,
    asiaNorthEurope: 5200,
    asiaMediterranean: 5800
  }, {
    month: 'Sep 2022',
    global: 2900,
    asiaNorthEurope: 4800,
    asiaMediterranean: 5400
  }, {
    month: 'Oct 2022',
    global: 2700,
    asiaNorthEurope: 4400,
    asiaMediterranean: 5000
  }, {
    month: 'Nov 2022',
    global: 2500,
    asiaNorthEurope: 4000,
    asiaMediterranean: 4600
  }, {
    month: 'Dec 2022',
    global: 2300,
    asiaNorthEurope: 3600,
    asiaMediterranean: 4200
  }, {
    month: 'Jan 2023',
    global: 2100,
    asiaNorthEurope: 3200,
    asiaMediterranean: 3800
  }, {
    month: 'Feb 2023',
    global: 1950,
    asiaNorthEurope: 2900,
    asiaMediterranean: 3500
  }, {
    month: 'Mar 2023',
    global: 1800,
    asiaNorthEurope: 2600,
    asiaMediterranean: 3200
  }, {
    month: 'Apr 2023',
    global: 1650,
    asiaNorthEurope: 2300,
    asiaMediterranean: 2900
  }, {
    month: 'May 2023',
    global: 1500,
    asiaNorthEurope: 2000,
    asiaMediterranean: 2600
  }, {
    month: 'Jun 2023',
    global: 1400,
    asiaNorthEurope: 1800,
    asiaMediterranean: 2400
  }, {
    month: 'Jul 2023',
    global: 1350,
    asiaNorthEurope: 1700,
    asiaMediterranean: 2300
  }, {
    month: 'Aug 2023',
    global: 1300,
    asiaNorthEurope: 1600,
    asiaMediterranean: 2200
  }, {
    month: 'Sep 2023',
    global: 1250,
    asiaNorthEurope: 1500,
    asiaMediterranean: 2100
  }, {
    month: 'Oct 2023',
    global: 1200,
    asiaNorthEurope: 1400,
    asiaMediterranean: 2000
  }, {
    month: 'Nov 2023',
    global: 1180,
    asiaNorthEurope: 1350,
    asiaMediterranean: 1950
  }, {
    month: 'Dec 2023',
    global: 1150,
    asiaNorthEurope: 1300,
    asiaMediterranean: 1900
  }, {
    month: 'Jan 2024',
    global: 1200,
    asiaNorthEurope: 1400,
    asiaMediterranean: 2000
  }, {
    month: 'Feb 2024',
    global: 1350,
    asiaNorthEurope: 1650,
    asiaMediterranean: 2300
  }, {
    month: 'Mar 2024',
    global: 1500,
    asiaNorthEurope: 1900,
    asiaMediterranean: 2600
  }, {
    month: 'Apr 2024',
    global: 1800,
    asiaNorthEurope: 2400,
    asiaMediterranean: 3200
  }, {
    month: 'May 2024',
    global: 2200,
    asiaNorthEurope: 3000,
    asiaMediterranean: 3900
  }, {
    month: 'Jun 2024',
    global: 2800,
    asiaNorthEurope: 3800,
    asiaMediterranean: 4800
  }, {
    month: 'Jul 2024',
    global: 3200,
    asiaNorthEurope: 4400,
    asiaMediterranean: 5400
  }, {
    month: 'Aug 2024',
    global: 3000,
    asiaNorthEurope: 4100,
    asiaMediterranean: 5100
  }, {
    month: 'Sep 2024',
    global: 2700,
    asiaNorthEurope: 3600,
    asiaMediterranean: 4600
  }, {
    month: 'Oct 2024',
    global: 2400,
    asiaNorthEurope: 3200,
    asiaMediterranean: 4100
  }, {
    month: 'Nov 2024',
    global: 2100,
    asiaNorthEurope: 2800,
    asiaMediterranean: 3600
  }, {
    month: 'Dec 2024',
    global: 1900,
    asiaNorthEurope: 2500,
    asiaMediterranean: 3200
  }, {
    month: 'Jan 2025',
    global: 1750,
    asiaNorthEurope: 2300,
    asiaMediterranean: 2900
  }, {
    month: 'Feb 2025',
    global: 1650,
    asiaNorthEurope: 2100,
    asiaMediterranean: 2700
  }, {
    month: 'Mar 2025',
    global: 1600,
    asiaNorthEurope: 2000,
    asiaMediterranean: 2600
  }, {
    month: 'Apr 2025',
    global: 1580,
    asiaNorthEurope: 1950,
    asiaMediterranean: 2550
  }, {
    month: 'May 2025',
    global: 1560,
    asiaNorthEurope: 1900,
    asiaMediterranean: 2500
  }, {
    month: 'Jun 2025',
    global: 1540,
    asiaNorthEurope: 1850,
    asiaMediterranean: 2450
  }, {
    month: 'Jul 2025',
    global: 1555,
    asiaNorthEurope: 1875,
    asiaMediterranean: 2475
  }, {
    month: 'Aug 2025',
    global: 1545,
    asiaNorthEurope: 1860,
    asiaMediterranean: 2460
  }, {
    month: 'Sep 2025',
    global: 1548,
    asiaNorthEurope: 1865,
    asiaMediterranean: 2465
  }, {
    month: 'Oct 2025',
    global: 1551,
    asiaNorthEurope: 1870,
    asiaMediterranean: 2470
  }];

  // Air Freight Asia-Europe Data (Jan 2022 - Oct 2025) - CTK (Demand) and ACTK (Capacity) in millions
  const airFreightData = [
    { month: 'Jan 2022', demand: 2850, capacity: 3200 },
    { month: 'Feb 2022', demand: 2920, capacity: 3180 },
    { month: 'Mar 2022', demand: 2780, capacity: 3150 },
    { month: 'Apr 2022', demand: 2650, capacity: 3100 },
    { month: 'May 2022', demand: 2580, capacity: 3050 },
    { month: 'Jun 2022', demand: 2720, capacity: 3080 },
    { month: 'Jul 2022', demand: 2890, capacity: 3120 },
    { month: 'Aug 2022', demand: 2950, capacity: 3140 },
    { month: 'Sep 2022', demand: 2820, capacity: 3110 },
    { month: 'Oct 2022', demand: 2750, capacity: 3090 },
    { month: 'Nov 2022', demand: 2680, capacity: 3070 },
    { month: 'Dec 2022', demand: 2600, capacity: 3040 },
    { month: 'Jan 2023', demand: 2520, capacity: 3020 },
    { month: 'Feb 2023', demand: 2480, capacity: 3000 },
    { month: 'Mar 2023', demand: 2550, capacity: 3030 },
    { month: 'Apr 2023', demand: 2620, capacity: 3060 },
    { month: 'May 2023', demand: 2690, capacity: 3090 },
    { month: 'Jun 2023', demand: 2780, capacity: 3130 },
    { month: 'Jul 2023', demand: 2850, capacity: 3170 },
    { month: 'Aug 2023', demand: 2920, capacity: 3200 },
    { month: 'Sep 2023', demand: 2880, capacity: 3180 },
    { month: 'Oct 2023', demand: 2840, capacity: 3160 },
    { month: 'Nov 2023', demand: 2790, capacity: 3140 },
    { month: 'Dec 2023', demand: 2720, capacity: 3110 },
    { month: 'Jan 2024', demand: 2680, capacity: 3080 },
    { month: 'Feb 2024', demand: 2750, capacity: 3120 },
    { month: 'Mar 2024', demand: 2820, capacity: 3160 },
    { month: 'Apr 2024', demand: 2890, capacity: 3200 },
    { month: 'May 2024', demand: 2950, capacity: 3240 },
    { month: 'Jun 2024', demand: 3020, capacity: 3280 },
    { month: 'Jul 2024', demand: 3080, capacity: 3320 },
    { month: 'Aug 2024', demand: 3140, capacity: 3360 },
    { month: 'Sep 2024', demand: 3100, capacity: 3340 },
    { month: 'Oct 2024', demand: 3060, capacity: 3320 },
    { month: 'Nov 2024', demand: 3020, capacity: 3300 },
    { month: 'Dec 2024', demand: 2980, capacity: 3280 },
    { month: 'Jan 2025', demand: 2940, capacity: 3260 },
    { month: 'Feb 2025', demand: 2900, capacity: 3240 },
    { month: 'Mar 2025', demand: 2960, capacity: 3270 },
    { month: 'Apr 2025', demand: 3020, capacity: 3300 },
    { month: 'May 2025', demand: 3080, capacity: 3330 },
    { month: 'Jun 2025', demand: 3140, capacity: 3360 },
    { month: 'Jul 2025', demand: 3180, capacity: 3380 },
    { month: 'Aug 2025', demand: 3220, capacity: 3400 },
    { month: 'Sep 2025', demand: 3260, capacity: 3420 },
    { month: 'Oct 2025', demand: 3300, capacity: 3450 }
  ];

  const handleBack = () => {
    window.history.back();
  };
  return <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={handleBack} className="mb-4 hover:bg-blue-100">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Portal
          </Button>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Monthly Logistics Insights Report</h1>
                <p className="text-xl text-gray-600 mt-2">November 2025 | For Executive Review</p>
              </div>
            </div>
            <LanguageSwitcher />
          </div>
          
          <div className="flex flex-wrap gap-2 mb-6">
            <Badge className="bg-blue-500">Market Analysis</Badge>
            <Badge variant="outline">November 2025</Badge>
            <Badge variant="outline">Executive Review</Badge>
          </div>
        </div>

        {/* Executive Summary */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <Target className="h-6 w-6 mr-3 text-blue-600" />
              {t('monthlyinsights.executive.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <img src="https://www.iru.org/sites/default/files/styles/1200x675/public/2024-05/European%20road%20freight%20rates%20Q1%202024%20Both%20spot%20and%20contract%20rates%20fall.png?h=cf2b8ed1&itok=lUfcXyeJ" alt="Road freight market overview" className="w-full h-48 object-cover rounded-lg shadow-lg mb-4" />
                <p className="text-gray-700 leading-relaxed">
                  {t('monthlyinsights.road.content')}
                </p>
                <div className="mt-4 p-4 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                  <p className="text-sm font-medium text-orange-800">
                    <strong>{t('monthlyinsights.road.impact').split(':')[0]}:</strong> {t('monthlyinsights.road.impact').split(':')[1]}
                  </p>
                </div>
              </div>
              
              <div>
                <img src="https://www.man-es.com/images/default-source/marine-images/istock-868192214_interp_container-stage-neu.jpg?sfvrsn=e1911eba_2" alt="Ocean freight market trends" className="w-full h-48 object-cover rounded-lg shadow-lg mb-4" />
                <p className="text-gray-700 leading-relaxed">
                  {t('monthlyinsights.ocean.content')}
                </p>
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                  <p className="text-sm font-medium text-blue-800">
                    <strong>{t('monthlyinsights.ocean.impact').split(':')[0]}:</strong> {t('monthlyinsights.ocean.impact').split(':')[1]}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div>
                <img src="https://www.agi.global/wp-content/uploads/2025/06/Airplane-on-airport-runway-against-sky_-1.webp" alt="Air freight capacity trends" className="w-full h-48 object-cover rounded-lg shadow-lg mb-4" />
                <p className="text-gray-700 leading-relaxed">
                  {t('monthlyinsights.air.content')}
                </p>
                <div className="mt-4 p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                  <p className="text-sm font-medium text-green-800">
                    <strong>{t('monthlyinsights.air.impact').split(':')[0]}:</strong> {t('monthlyinsights.air.impact').split(':')[1]}
                  </p>
                </div>
              </div>
              
              <div>
                <img 
                  src="https://panattonieurope.com/uploads/media/x1745/00/4160-DJI_0097.webp?v=1-0" 
                  alt="European logistics real estate and warehousing"
                  className="w-full h-48 object-cover rounded-lg shadow-lg mb-4"
                />
                <p className="text-gray-700 leading-relaxed mb-4">
                  {t('monthlyinsights.realestate.content')}
                </p>
                <div className="mt-4 p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
                  <p className="text-sm font-medium text-purple-800">
                    <strong>{t('monthlyinsights.realestate.impact').split(':')[0]}:</strong> {t('monthlyinsights.realestate.impact').split(':')[1]}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* European Road Freight Market */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <Truck className="h-6 w-6 mr-3 text-orange-600" />
              {t('monthlyinsights.roadfreight.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('monthlyinsights.roadfreight.intro')}
            </p>

            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
              {t('monthlyinsights.roadfreight.developments')}
            </h3>
            
            <div className="space-y-4 mb-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">{t('monthlyinsights.roadfreight.rates.title')}</h4>
                <p className="text-gray-700 text-sm">
                  {t('monthlyinsights.roadfreight.rates.content')}
                </p>
              </div>
              
              <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-400">
                <h4 className="font-semibold text-red-900 mb-2">{t('monthlyinsights.roadfreight.capacity.title')}</h4>
                <p className="text-red-800 text-sm">
                  {t('monthlyinsights.roadfreight.capacity.content')}
                </p>
              </div>
              
              <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
                <h4 className="font-semibold text-yellow-900 mb-2">{t('monthlyinsights.roadfreight.diesel.title')}</h4>
                <p className="text-yellow-800 text-sm">
                  {t('monthlyinsights.roadfreight.diesel.content')}
                </p>
              </div>
            </div>

            <div className="mb-6">
              <div className="bg-white p-6 rounded-lg shadow-lg border">
                <h4 className="text-lg font-semibold mb-4 text-center text-gray-900">European Road Freight Index & Fuel Price</h4>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={roadFreightData} margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 60
                  }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" stroke="#666" fontSize={10} angle={-45} textAnchor="end" height={80} interval={5} />
                      <YAxis yAxisId="freight" orientation="left" stroke="#2563eb" fontSize={12} domain={[90, 'dataMax']} label={{
                      value: 'Freight Index',
                      angle: -90,
                      position: 'insideLeft',
                      style: {
                        textAnchor: 'middle'
                      }
                    }} />
                      <YAxis yAxisId="fuel" orientation="right" stroke="#dc2626" fontSize={12} domain={[1350, 'dataMax']} label={{
                      value: 'Fuel Price (€/1000L)',
                      angle: 90,
                      position: 'insideRight',
                      style: {
                        textAnchor: 'middle'
                      }
                    }} />
                      <Tooltip contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #ccc',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }} formatter={(value, name) => [name === 'freightIndex' ? `${value}` : `€${value}/1000L`, name === 'freightIndex' ? 'Freight Index' : 'Fuel Price']} />
                      <Line yAxisId="freight" type="monotone" dataKey="freightIndex" stroke="#2563eb" strokeWidth={3} dot={{
                      fill: '#2563eb',
                      strokeWidth: 2,
                      r: 3
                    }} activeDot={{
                      r: 5,
                      fill: '#2563eb'
                    }} />
                      <Line yAxisId="fuel" type="monotone" dataKey="fuelPrice" stroke="#dc2626" strokeWidth={3} dot={{
                      fill: '#dc2626',
                      strokeWidth: 2,
                      r: 3
                    }} activeDot={{
                      r: 5,
                      fill: '#dc2626'
                    }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center mt-4 space-x-6">
                  <div className="flex items-center">
                    <div className="w-4 h-0.5 bg-blue-600 mr-2"></div>
                    <span className="text-sm text-gray-600">Freight Index</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-0.5 bg-red-600 mr-2"></div>
                    <span className="text-sm text-gray-600">Fuel Price (€/1000L)</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Sources: <a href="https://www.iru.org/intelligence/road-transport-intelligence/european-road-freight-rate-development-benchmark-q2-2025" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">IRU</a>/<a href="https://ti-insight.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Ti</a>/<a href="https://market-insights.upply.com/en/the-outlook-for-european-road-transport-in-2025" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Upply</a>, <a href="https://www.transporeon.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Transporeon</a>, <a href="https://energy.ec.europa.eu/data-and-analysis/weekly-oil-bulletin_en" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">EU Weekly Oil Bulletin</a></p>
            </div>

            <div className="bg-blue-600 text-white p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-4 flex items-center">
                <Target className="h-5 w-5 mr-2" />
                {t('monthlyinsights.roadfreight.actions.title')}
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <Users className="h-4 w-4 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-sm">{t('monthlyinsights.roadfreight.actions.diversify.title')}</p>
                      <p className="text-xs opacity-90">{t('monthlyinsights.roadfreight.actions.diversify.content')}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Package className="h-4 w-4 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-sm">{t('monthlyinsights.roadfreight.actions.prepulls.title')}</p>
                      <p className="text-xs opacity-90">{t('monthlyinsights.roadfreight.actions.prepulls.content')}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <Clock className="h-4 w-4 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-sm">{t('monthlyinsights.roadfreight.actions.slotting.title')}</p>
                      <p className="text-xs opacity-90">{t('monthlyinsights.roadfreight.actions.slotting.content')}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Calendar className="h-4 w-4 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-sm">{t('monthlyinsights.roadfreight.actions.weekend.title')}</p>
                      <p className="text-xs opacity-90">{t('monthlyinsights.roadfreight.actions.weekend.content')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Global Ocean Freight Market */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <Ship className="h-6 w-6 mr-3 text-blue-600" />
              {t('monthlyinsights.oceanfreight.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('monthlyinsights.oceanfreight.intro')}
            </p>

            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
              {t('monthlyinsights.oceanfreight.rates.title')}
            </h3>
            
            <div className="mb-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('monthlyinsights.oceanfreight.rates.content')}
              </p>
              
              <div className="bg-white p-6 rounded-lg shadow-lg border mb-2">
                <h4 className="text-lg font-semibold mb-4 text-center text-gray-900">Shanghai Containerized Freight Index (SCFI)</h4>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={scfiData} margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 60
                  }} className="recharts-surface rounded-none">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" stroke="#666" fontSize={10} angle={-45} textAnchor="end" height={80} interval={5} />
                      <YAxis stroke="#666" fontSize={12} label={{
                      value: 'USD/TEU',
                      angle: -90,
                      position: 'insideLeft',
                      style: {
                        textAnchor: 'middle'
                      }
                    }} />
                      <Tooltip contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #ccc',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }} formatter={(value, name) => [`$${value}/TEU`, name === 'global' ? 'Global' : name === 'asiaNorthEurope' ? 'Asia - North Europe' : 'Asia - Mediterranean']} />
                      <Line type="monotone" dataKey="global" stroke="#2563eb" strokeWidth={3} dot={{
                      fill: '#2563eb',
                      strokeWidth: 2,
                      r: 2
                    }} activeDot={{
                      r: 4,
                      fill: '#2563eb'
                    }} />
                      <Line type="monotone" dataKey="asiaNorthEurope" stroke="#dc2626" strokeWidth={3} dot={{
                      fill: '#dc2626',
                      strokeWidth: 2,
                      r: 2
                    }} activeDot={{
                      r: 4,
                      fill: '#dc2626'
                    }} />
                      <Line type="monotone" dataKey="asiaMediterranean" stroke="#16a34a" strokeWidth={3} dot={{
                      fill: '#16a34a',
                      strokeWidth: 2,
                      r: 2
                    }} activeDot={{
                      r: 4,
                      fill: '#16a34a'
                    }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center mt-4 space-x-6">
                  <div className="flex items-center">
                    <div className="w-4 h-0.5 bg-blue-600 mr-2"></div>
                    <span className="text-sm text-gray-600">Global</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-0.5 bg-red-600 mr-2"></div>
                    <span className="text-sm text-gray-600">Asia - North Europe</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-0.5 bg-green-600 mr-2"></div>
                    <span className="text-sm text-gray-600">Asia - Mediterranean</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500">Sources: <a href="https://en.sse.net.cn/indices/scfinew.jsp" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Shanghai Shipping Exchange</a>, <a href="https://www.drewry.co.uk/supply-chain-advisors/supply-chain-expertise/world-container-index-assessed-by-drewry" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Drewry Maritime Research</a></p>
            </div>

            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-yellow-600" />
              {t('monthlyinsights.oceanfreight.reliability.title')}
            </h3>
            
            <div className="mb-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('monthlyinsights.oceanfreight.reliability.content')}
              </p>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 px-4 py-2 text-left">Alliance/Carrier</th>
                      <th className="border border-gray-300 px-4 py-2 text-center">Global</th>
                      <th className="border border-gray-300 px-4 py-2 text-center">Asia-North Europe</th>
                      <th className="border border-gray-300 px-4 py-2 text-center">Asia-Mediterranean</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-medium">Gemini Alliance</td>
                      <td className="border border-gray-300 px-4 py-2 text-center text-green-600 font-semibold">89.1%</td>
                      <td className="border border-gray-300 px-4 py-2 text-center text-green-600 font-semibold">98.2%</td>
                      <td className="border border-gray-300 px-4 py-2 text-center text-yellow-600 font-semibold">74.0%</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 font-medium">Ocean Alliance</td>
                      <td className="border border-gray-300 px-4 py-2 text-center text-yellow-600 font-semibold">68.6%</td>
                      <td className="border border-gray-300 px-4 py-2 text-center text-yellow-600 font-semibold">70.9%</td>
                      <td className="border border-gray-300 px-4 py-2 text-center text-red-600 font-semibold">62.1%</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-medium">MSC</td>
                      <td className="border border-gray-300 px-4 py-2 text-center text-yellow-600 font-semibold">65.9%</td>
                      <td className="border border-gray-300 px-4 py-2 text-center text-red-600 font-semibold">64.4%</td>
                      <td className="border border-gray-300 px-4 py-2 text-center text-yellow-600 font-semibold">67.4%</td>
                    </tr>
                    <tr className="bg-blue-50 font-semibold">
                      <td className="border border-gray-300 px-4 py-2">Industry Average</td>
                      <td className="border border-gray-300 px-4 py-2 text-center">65.2%</td>
                      <td className="border border-gray-300 px-4 py-2 text-center">67.4%</td>
                      <td className="border border-gray-300 px-4 py-2 text-center">56.9%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mb-4">Source: Sea-Intelligence, September 2025. Note: Global data is for Aug/Sep 2025. Asia-Europe lane data is from July 2025.</p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-400">
                <h4 className="font-semibold text-red-900 mb-2">{t('monthlyinsights.oceanfreight.rotterdam.title')}</h4>
                <p className="text-red-800 text-sm">
                  {t('monthlyinsights.oceanfreight.rotterdam.content')}
                </p>
              </div>
              
              <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
                <h4 className="font-semibold text-yellow-900 mb-2">{t('monthlyinsights.oceanfreight.gdansk.title')}</h4>
                <p className="text-yellow-800 text-sm">
                  {t('monthlyinsights.oceanfreight.gdansk.content')}
                </p>
              </div>
              
              <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                <h4 className="font-semibold text-green-900 mb-2">{t('monthlyinsights.oceanfreight.koper.title')}</h4>
                <p className="text-green-800 text-sm">
                  {t('monthlyinsights.oceanfreight.koper.content')}
                </p>
              </div>
            </div>

            <div className="bg-blue-600 text-white p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-4 flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                {t('monthlyinsights.oceanfreight.buffers.title')}
              </h4>
              <p className="text-sm mb-4 opacity-90">{t('monthlyinsights.oceanfreight.buffers.intro')}</p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white/10 p-4 rounded-lg">
                  <h5 className="font-semibold mb-2">{t('monthlyinsights.oceanfreight.buffers.north.title')}</h5>
                  <p className="text-sm">{t('monthlyinsights.oceanfreight.buffers.north.content')}</p>
                </div>
                <div className="bg-white/10 p-4 rounded-lg">
                  <h5 className="font-semibold mb-2">{t('monthlyinsights.oceanfreight.buffers.med.title')}</h5>
                  <p className="text-sm">{t('monthlyinsights.oceanfreight.buffers.med.content')}</p>
                </div>
                <div className="bg-white/10 p-4 rounded-lg">
                  <h5 className="font-semibold mb-2">{t('monthlyinsights.oceanfreight.buffers.baltic.title')}</h5>
                  <p className="text-sm">{t('monthlyinsights.oceanfreight.buffers.baltic.content')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Global Air Freight Market */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <Plane className="h-6 w-6 mr-3 text-green-600" />
              {t('monthlyinsights.airfreight.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('monthlyinsights.airfreight.intro')}
            </p>

            <div className="mb-6">
              <div className="bg-white p-6 rounded-lg shadow-lg border mb-2">
                <h4 className="text-lg font-semibold mb-4 text-center text-gray-900">Air Freight Asia-Europe: Demand vs Capacity</h4>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={airFreightData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="month" 
                        stroke="#666"
                        fontSize={10}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        interval={5}
                      />
                      <YAxis 
                        stroke="#666"
                        fontSize={12}
                        domain={[2000, 'dataMax']}
                        label={{ value: 'Million CTK/ACTK', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #ccc', 
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}
                        formatter={(value, name) => [
                          `${value}M`,
                          name === 'demand' ? 'Demand (CTK)' : 'Capacity (ACTK)'
                        ]}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="demand" 
                        stroke="#2563eb" 
                        strokeWidth={3}
                        dot={{ fill: '#2563eb', strokeWidth: 2, r: 2 }}
                        activeDot={{ r: 4, fill: '#2563eb' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="capacity" 
                        stroke="#dc2626" 
                        strokeWidth={3}
                        dot={{ fill: '#dc2626', strokeWidth: 2, r: 2 }}
                        activeDot={{ r: 4, fill: '#dc2626' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center mt-4 space-x-6">
                  <div className="flex items-center">
                    <div className="w-4 h-0.5 bg-blue-600 mr-2"></div>
                    <span className="text-sm text-gray-600">Demand (CTK)</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-0.5 bg-red-600 mr-2"></div>
                    <span className="text-sm text-gray-600">Capacity (ACTK)</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500">Sources: <a href="https://www.iata.org/en/pressroom/2025-releases/2025-10-29-01/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">IATA</a>, <a href="https://www.dhl.com/us-en/home/global-forwarding/latest-news-and-webinars/air-freight-market-update.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">DHL Global Forwarding</a></p>
            </div>

            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
              {t('monthlyinsights.airfreight.regulatory.title')}
            </h3>
            
            <div className="space-y-4 mb-6">
              <div className="p-4 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-semibold text-orange-900 mb-2">{t('monthlyinsights.airfreight.ics2.title')}</h4>
                <p className="text-orange-800 text-sm">
                  {t('monthlyinsights.airfreight.ics2.content')}
                </p>
              </div>
              
              <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-400">
                <h4 className="font-semibold text-red-900 mb-2">{t('monthlyinsights.airfreight.saf.title')}</h4>
                <p className="text-red-800 text-sm">
                  {t('monthlyinsights.airfreight.saf.content')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* European Real Estate & Warehousing */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <Building className="h-6 w-6 mr-3 text-purple-600" />
              {t('monthlyinsights.realestate.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('monthlyinsights.realestate.intro')}
            </p>

            <div className="space-y-4 mb-6">
              <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-400">
                <h4 className="font-semibold text-red-900 mb-2">{t('monthlyinsights.realestate.slowdown.title')}</h4>
                <p className="text-red-800 text-sm">
                  {t('monthlyinsights.realestate.slowdown.content')}
                </p>
              </div>
              
              <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                <h4 className="font-semibold text-green-900 mb-2">{t('monthlyinsights.realestate.rents.title')}</h4>
                <p className="text-green-800 text-sm">
                  {t('monthlyinsights.realestate.rents.content')}
                </p>
              </div>
              
              <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-semibold text-blue-900 mb-2">{t('monthlyinsights.realestate.demand.title')}</h4>
                <p className="text-blue-800 text-sm">
                  {t('monthlyinsights.realestate.demand.content')}
                </p>
              </div>
              
              <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
                <h4 className="font-semibold text-yellow-900 mb-2">{t('monthlyinsights.realestate.spain.title')}</h4>
                <p className="text-yellow-800 text-sm">
                  {t('monthlyinsights.realestate.spain.content')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <Card className="bg-gray-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Confidential. For internal use only.</strong> Report generated on 2025-11-04.
              </p>
              <p className="text-xs text-gray-500">
                This report is based on publicly available data and is intended for informational purposes only. While every effort has been made to ensure accuracy, we are not liable for any errors or omissions.
              </p>
            </div>
            
            <Separator className="my-6" />
            
            <div className="text-xs text-gray-500">
              <h4 className="font-semibold mb-2">Key Sources:</h4>
              <div className="grid md:grid-cols-2 gap-2">
                <div>
                  <p>• <a href="https://ti-insight.com/briefs/european-road-freight-market-update-q3-2025/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Ti Insight - European Road Freight Market</a></p>
                  <p>• <a href="https://ti-insight.com/briefs/europes-truck-drivers-shortage-challenges-and-responses/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">IRU - Driver Shortage Analysis</a></p>
                  <p>• <a href="https://en.sse.net.cn/indices/scfinew.jsp" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Shanghai Shipping Exchange - SCFI</a></p>
                  <p>• <a href="https://www.drewry.co.uk/supply-chain-advisors/supply-chain-expertise/world-container-index-assessed-by-drewry" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Drewry Maritime Research - WCI</a></p>
                  <p>• <a href="https://www.sea-intelligence.com/press-room/353-global-container-reliability-nears-six-year-september-high" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Sea-Intelligence - Schedule Reliability</a></p>
                </div>
                <div>
                  <p>• <a href="https://www.iata.org/en/pressroom/2025-releases/2025-10-29-01/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">IATA - Air Cargo Market Analysis</a></p>
                  <p>• <a href="https://energy.ec.europa.eu/data-and-analysis/weekly-oil-bulletin_en" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">European Commission - Weekly Oil Bulletin</a></p>
                  <p>• <a href="https://www.cbre.com/insights/figures/france-logistics-figures-q3-2025" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">CBRE - European Real Estate</a></p>
                  <p>• <a href="https://www.cushmanwakefield.com/en/germany/insights/germany-marketbeat" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Cushman & Wakefield - Market Reports</a></p>
                  <p>• <a href="https://www.portgdansk.pl/en/events/port-of-gdansk-maintains-its-strong-position-in-the-first-half-of-2025/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Port Authorities - Performance Data</a></p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
};
export default MonthlyLogisticsInsightsArticle;